import React, { FC, useEffect, useState } from 'react';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { requiresSwedishPinValidation } from 'utils/personal-identity-number';
import { OrderInputType } from 'models/order-input/shared';
import { LabOrderContext } from './lab-order-context';
import { LabOrderInput } from 'models/order-input/lab-order';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Box, CircularProgress } from '@mui/material';
import OrderFormDetails from 'components/order-forms/shared/order-form-details.component';
import { OrderForm } from 'models/order-form';
import { orderFormTemplate } from 'apollo/templates/order-form';
import LabOrder from './lab-order.component';
import { dentistsWithClinicForPrepopulateTemplate } from 'apollo/templates/user';
import { extractConstructions, extractDentistSearchText } from 'components/order-flow/lab-order/order-form-helper';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { getClinicTemplate } from 'apollo/templates/organization';
import { useAssignInternalDentistId } from 'hooks/useAssignInternalDentistId';
import { resolvePrepopulatedDeliveryClinic } from 'utils/lab-dentist-default-clinic';

const LabOrderFromOrderForm: FC = () => {
    const { showBoundary } = useDenthubErrorBoundary();
    const [order, setOrder] = useState<LabOrderInput>({} as LabOrderInput);
    const [isTioTandvardPricingEnabled, setIsTioTandvardPricingEnabled] = useState<boolean>(false);
    const { orderFormId } = useParams<{ orderFormId: string }>();
    const [form, setForm] = useState<OrderForm>({} as OrderForm);
    const [loading, setLoading] = useState<boolean>(true);
    const currentOrg = currentOrgVar();
    const { assignIdToDentist } = useAssignInternalDentistId();

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    const [orderForm] = useLazyQuery(orderFormTemplate, {
        onCompleted: ({ orderForm }) => {
            setForm(orderForm);
            setOrder({
                inputType: OrderInputType.NEW,
                supplier: { _id: currentOrg._id },
                isActionBasedOrder: false,
                customer: undefined,
                dentist: undefined,
                patient: {
                    personalIdentityNumber: orderForm.formFields?.patientPersonalIdentityNumber?.value || '',
                    firstName: orderForm.formFields?.patientFirstName?.value || '',
                    lastName: orderForm.formFields?.patientLastName?.value || '',
                    noPersonalIdentityNumber: requiresSwedishPinValidation(currentOrg)
                        ? orderForm.formFields?.isValidPinFormat === false
                        : !orderForm.formFields?.patientPersonalIdentityNumber?.value,
                },
                constructions: extractConstructions(orderForm),
                comment: orderForm.formFields?.constructionComment?.value || '',
                files: [],
                historicalComments: [],
                sample: { type: '', content: [], otherContent: '' },
                deliveryDetails: {
                    date: orderForm.deliveryDate
                        ? new Date(new Date(orderForm.deliveryDate).setHours(0, 0, 0, 0))
                        : new Date(),
                    destination: undefined,
                    pickUpLocation: undefined,
                    time: orderForm.deliveryTime,
                },
                sampleReceivedDate: new Date(),
                internalDeliveryDate: null,
                externalDeliveryDate: null,
                productionPlanningSteps: [],
                orderForm: orderForm,
            } as LabOrderInput);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [getDentists] = useLazyQuery(dentistsWithClinicForPrepopulateTemplate, {
        onCompleted: async ({ dentistsWithClinicForPrepopulate: searchedDentists }) => {
            if (searchedDentists && searchedDentists.length > 0) {
                const [matchedDentist] = searchedDentists;
                // The dentist is attached to the order here rather than picked by hand, so
                // number them at this point too.
                const dentistLocalIds = await assignIdToDentist(matchedDentist._id, matchedDentist.clinic?._id);
                const defaultDeliveryClinic = resolvePrepopulatedDeliveryClinic(matchedDentist);
                setOrder((prevOrder) => ({
                    ...prevOrder,
                    dentist: dentistLocalIds ? { ...matchedDentist, dentistLocalIds } : matchedDentist,
                    deliveryDetails: {
                        ...prevOrder.deliveryDetails,
                        destination: defaultDeliveryClinic,
                        pickUpLocation: defaultDeliveryClinic,
                    },
                }));
                await getClinic({ variables: { id: matchedDentist.clinic._id } });
            }
            setLoading(false);
        },
        onError: (err) => {
            showBoundary(err);
        },
    });

    const [getClinic] = useLazyQuery(getClinicTemplate, {
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: async ({ clinic }) => {
            const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
                clinic.parentClinicId ? clinic.parentClinic.customerSettings : clinic.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
            );
            const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
                currentOrg.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
            );
            // orderVersion is the backend's resolved answer - parent-clinic inheritance and the
            // local-clinic rule are already applied - so read it instead of re-deriving it from
            // the customer settings here.
            const isActionBasedClinic = clinic.orderVersion === CONSTANTS.SETTINGS_VALUES.ACTION_BASED;
            setIsTioTandvardPricingEnabled(
                isActionBasedClinic ? isTioTandvardPricingEnabled : isTioTandvardPricingForLabEnabled,
            );
            setOrder((prev) => {
                const updatedOrder = {
                    ...prev,
                    // Single writer of order.customer. clinic._id and isActionBasedOrder have to
                    // land in the same update: _id is what triggers the construction step to
                    // fetch a price list, and isActionBasedOrder is what picks which one.
                    customer: clinic,
                    isActionBasedOrder: isActionBasedClinic,
                };
                if (clinic.local || clinic.orderVersion === 'Simple') {
                    updatedOrder.deliveryDetails = {
                        ...prev.deliveryDetails,
                        pickUpLocation: clinic,
                        destination: clinic,
                    };
                }
                return updatedOrder;
            });
        },
    });

    useEffect(() => {
        if (order.orderForm) {
            const dentistSearchText = extractDentistSearchText(order.orderForm);
            if (dentistSearchText !== '' && order.orderForm.formFields?.clinicName?.value) {
                getDentists({
                    variables: {
                        dentistName: dentistSearchText,
                        clinicName: order.orderForm.formFields.clinicName.value,
                    },
                });
            }
            return;
        }
        setLoading(false);
    }, [order.orderForm]);

    useEffect(() => {
        if (orderFormId) {
            orderForm({ variables: { _id: orderFormId } });
            return;
        }
        setLoading(false);
    }, [orderFormId]);

    return (
        <LabOrderContext.Provider
            value={{
                order,
                setOrder,
                isTioTandvardPricingEnabled,
                setIsTioTandvardPricingEnabled,
                isProductionPlanningEnabled,
            }}
        >
            {loading || !order.supplier ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <Box>
                        <CircularProgress size={80} />
                    </Box>
                </Box>
            ) : (
                <Box display="flex">
                    <Box flexGrow={1}>
                        <LabOrder />
                    </Box>
                    <Box
                        sx={{
                            flexShrink: 0,
                            marginLeft: 2,
                            position: 'sticky',
                            top: '70px',
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {/* leave the order form at least ~45% of the window */}
                        <OrderFormDetails orderForm={form} height="80vh" maxImageWidth="55vw" />
                    </Box>
                </Box>
            )}
        </LabOrderContext.Provider>
    );
};

export default LabOrderFromOrderForm;
